import { Place, PlaceType, ScoredPlace } from "@gen/place/v1/place_pb";
import { search } from "@services/place";
import theme from "@theme";
import { Icon, Input } from "@thetreep/ui";
import { useEffect, useState } from "react";

export interface PlaceInputProps {
  value: Place;
  disabled?: boolean;
  filter?: PlaceType[];
  placeholder?: string;
  onChange: (place?: Place) => void;
}

export function PlaceInput({
  value,
  disabled,
  filter,
  placeholder,
  onChange,
}: PlaceInputProps) {
  const [query, setQuery] = useState("");
  const [places, setPlaces] = useState<ScoredPlace[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [prevValue, setPrevValue] = useState<Place | undefined>(undefined);

  if (value && value !== prevValue) {
    setPrevValue(value);
    setQuery(value.name);
    setPlaces([]);
    setIsOpen(false);
  }

  useEffect(() => {
    (async () => {
      if (!query || query === "") {
        return;
      }
      const scoredPlaces = await search(query, filter);
      setPlaces(scoredPlaces);
    })();
  }, [query]);

  return (
    <div className="relative h-full">
      <div className="h-full [&_div]:hover:!border-cnam-light-300 [&_div]:max-h-full">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          disabled={disabled}
          placeholder={placeholder}
          startEnhancer={
            <Icon
              src="search_line"
              color={theme.colors.cnam.blue.DEFAULT}
              size={18}
            />
          }
        />
      </div>
      {isOpen && places.length > 0 && (
        <PlacesAutocomplete
          className="absolute z-10 mt-2 border rounded-md border-cnam-light-300 max-h-[348px]"
          places={places}
          onChange={(p) => onChange(p)}
        />
      )}
    </div>
  );
}

interface PlacesAutocompleteProps {
  className?: string;
  places: ScoredPlace[];
  preserveOrder?: boolean;
  onChange: (place: Place) => void;
}

export function PlacesAutocomplete({
  className,
  places,
  preserveOrder,
  onChange,
}: PlacesAutocompleteProps) {
  function getIconSrcFromPlaceType(type: PlaceType): string {
    switch (type) {
      case PlaceType.CITY:
        return "location_line";
      case PlaceType.TRAIN_STATION:
        return "train_4_line";
      case PlaceType.AIRPORT:
        return "airplane_line";
      default:
        return "location_line";
    }
  }

  function getCountryFlag(code: string | undefined) {
    switch (code?.toLowerCase()) {
      case "fr":
        return "🇫🇷";
      default:
        return "";
    }
  }

  function getInfo(p: Place) {
    return [p.zipCode, `${p.country?.name} ${getCountryFlag(p.country?.code)}`]
      .filter((i) => !!i && i != "")
      .join(", ");
  }

  return (
    <div
      className={`
            bg-surface
            flex flex-col w-full
            overflow-auto
            ${className}
            `}
    >
      {(preserveOrder ? places : [...places].sort((a, b) => b.score - a.score))
        .map((sp) => sp.place)
        .filter((p) => !!p)
        .map((p) => (
          <div
            className="
                        flex items-center gap-2 
                        p-4 border-b w-full
                        cursor-pointer
                        hover:bg-surface-soft
                        "
            onMouseDown={() => onChange(p)}
            key={p.id}
          >
            <Icon
              src={getIconSrcFromPlaceType(p.type)}
              color={theme.colors.text.strong}
              size={18}
            />
            <span className="text-sm">{p.name}</span>
            <span className="text-xs text-text-strong flex-grow text-right">
              {getInfo(p)}
            </span>
          </div>
        ))}
    </div>
  );
}
